/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useContext, useEffect } from "react";
import { Button, TextField, Autocomplete } from "@mui/material";
import GenericModal from "../../Modal/GenericModal";
import { Vocabulary } from "../../Utils/Vocabulary";
import ClientDropdown, { ClientData } from "../Generic/ClientDropdown";
import VehicleSearchModal from "../Generic/VehicleSearchModal";
import AntecontractFields, {
  AntecontractFieldsData,
} from "../Generic/AntecontractFields";
import { clientDocType } from "../../Utils/autocompleteEnum";
import { CacheContext } from "../../Context/cacheContext";
import { postData } from "../../Services/postData";
import { urlEnum } from "../../Utils/urlEnum";
import { toast } from "react-toastify";
import PreviewPDF from "../Generic/PDFViewer";

// Document type constants from clientDocType
const DOC_TYPE_GDPR = clientDocType.find((t) => t.id === 0)?.nume;
const DOC_TYPE_ANTECONTRACT = clientDocType.find((t) => t.id === 1)?.nume;

type AddDocumentModalProps = {
  open: boolean;
  onClose: () => void;
  onGenerate?: (templateData: any) => void;
};

export default function AddDocumentModal({
  open,
  onClose,
  onGenerate,
}: AddDocumentModalProps) {
  const [generateData, setGenerateData] = useState({
    name: "",
    type: "",
    description: "",
    content: "",
    tip_fisier: null as any,
    companyName: "",
    dpo: "",
  });

  // Antecontract specific fields using shared type
  const [antecontractData, setAntecontractData] =
    useState<AntecontractFieldsData>({
      firma: null,
      advanceInvoiceNumber: "",
      advancePaymentDate: null,
      carPrice: "",
      advanceValue: "",
      remainingPayment: "",
      exchangeRate: "",
      vehicleId: "",
      vehicleName: "",
      VIN: "",
    });

  const [openVehicleSearch, setOpenVehicleSearch] = useState(false);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);

  const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
  const cacheContext = useContext(CacheContext);

  const isAntecontract =
    generateData.tip_fisier?.nume === DOC_TYPE_ANTECONTRACT;
  const isGDPR = generateData.tip_fisier?.nume === DOC_TYPE_GDPR;

  useEffect(() => {
    if (cacheContext?.cache?.company && cacheContext.cache.company.length > 0) {
      setCompaniesLoaded(true);
    }
  }, [cacheContext?.cache?.company]);

  const handleGenerateInputChange = (field: string, value: any) => {
    setGenerateData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAntecontractChange = (
    field: keyof AntecontractFieldsData,
    value: unknown,
  ) => {
    setAntecontractData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectVehicle = (vehicle: any) => {
    setAntecontractData((prev) => ({
      ...prev,
      vehicleId: vehicle.id,
      vehicleName:
        `${vehicle.brand_r?.nume || ""} ${vehicle.model_r?.nume || ""}`.trim(),
      VIN: vehicle.vin || "",
    }));
    setOpenVehicleSearch(false);
  };

  const handleSubmit = async () => {
    if (!selectedClient || !generateData.tip_fisier || !antecontractData.firma)
      return;

    try {
      let res;

      if (isGDPR) {
        res = await postData(
          `${urlEnum.templates}/generateGDPR/${selectedClient.id}/${antecontractData.firma.id}`,
        );
      } else if (isAntecontract) {
        if (!antecontractData.vehicleId) {
          toast.error(Vocabulary.associateWithVehicle);
          return;
        }
        const remainingPayment =
          (parseFloat(antecontractData?.carPrice) || 0) -
          (parseFloat(antecontractData?.advanceValue) || 0);
          
        res = await postData(
          `${urlEnum.templates}/generateBeforeContract/${selectedClient.id}/${antecontractData.firma.id}/${antecontractData.vehicleId}`,
          {
            ...antecontractData,
            remainingPayment: remainingPayment.toString(),
          },
        );
      } else {
        if (onGenerate) {
          onGenerate({
            ...generateData,
            clientId: selectedClient.id,
            mode: "generate",
          });
        }
        handleClose();
        return;
      }

      if (res?.data?.client) {
        const fileName = res.data.client.filePath
          ? res.data.client.filePath.split("/").pop()
          : res.data.client.name?.split("/").pop();

        if (fileName) {
          toast.success(`${Vocabulary.docGenerated} ${fileName}`);

          const file = `${urlEnum.templates}/getDoc/${fileName}`;
          setPreviewData({
            id: selectedClient.id,
            file: file,
            fileName: fileName,
            email: selectedClient.email,
            filePath: res.data.client.filePath || res.data.client.name,
            isSignature: selectedClient.isSignature,
          });
          setOpenPreviewModal(true);
        }
      }
    } catch (error) {
      console.error("Error generating document:", error);
      toast.error("Error generating document");
    }
  };

  const handleClose = () => {
    setGenerateData({
      name: "",
      type: "",
      description: "",
      content: "",
      tip_fisier: null,
      companyName: "",
      dpo: "",
    });
    setAntecontractData({
      firma: null,
      advanceInvoiceNumber: "",
      advancePaymentDate: null,
      carPrice: "",
      advanceValue: "",
      remainingPayment: "",
      exchangeRate: "",
      vehicleId: "",
      vehicleName: "",
      VIN: "",
    });
    setOpenVehicleSearch(false);
    setOpenPreviewModal(false);
    setPreviewData(null);
    setSelectedClient(null);
    onClose();
  };

  const getGenerateValidation = () => {
    const baseValid = generateData.tip_fisier && selectedClient;
    if (!baseValid) return false;

    if (isGDPR) {
      return antecontractData.firma !== null;
    }
    if (isAntecontract) {
      return (
        antecontractData.firma !== null && antecontractData.vehicleId !== ""
      );
    }
    return generateData.content.trim() !== "";
  };

  const isFormValid = getGenerateValidation();

  const modalButtons = (
    <div style={{ display: "flex", gap: "10px" }}>
      <Button variant="contained" color="error" onClick={handleClose}>
        {Vocabulary.cancel}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={!isFormValid}
        style={{ color: "white" }}
      >
        {Vocabulary.generateDocument}
      </Button>
    </div>
  );

  return (
    <GenericModal
      open={open}
      onClose={handleClose}
      title={Vocabulary.generateDocument}
      buttons={modalButtons}
      maxWidth="md"
    >
      <div style={{ padding: "20px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <ClientDropdown
            value={selectedClient}
            onChange={setSelectedClient}
            label={Vocabulary.client}
            fullWidth
            required
          />

          <Autocomplete
            freeSolo={false}
            size="small"
            disableClearable
            disablePortal
            getOptionLabel={(option) => option?.nume || ""}
            isOptionEqualToValue={(option, value) =>
              option?.nume === value?.nume
            }
            value={generateData.tip_fisier}
            onChange={(_, value) =>
              handleGenerateInputChange("tip_fisier", value)
            }
            options={clientDocType}
            renderInput={(params) => (
              <TextField {...params} label={Vocabulary.tipDocument} required />
            )}
          />

          {(isGDPR || isAntecontract) && (
            <Autocomplete
              freeSolo={false}
              style={{ width: "100%" }}
              size="small"
              disablePortal
              value={antecontractData.firma}
              onChange={(_, value) => handleAntecontractChange("firma", value)}
              getOptionLabel={(option: any) => option?.nume_firma || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              options={cacheContext?.cache?.company || []}
              loading={!companiesLoaded}
              renderInput={(params) => (
                <TextField {...params} label={Vocabulary.nume_firma} required />
              )}
            />
          )}

          {isAntecontract && (
            <AntecontractFields
              data={antecontractData}
              onChange={handleAntecontractChange}
              onOpenVehicleSearch={() => setOpenVehicleSearch(true)}
              showFirma={false}
            />
          )}
        </div>
      </div>

      <VehicleSearchModal
        open={openVehicleSearch}
        onClose={() => setOpenVehicleSearch(false)}
        onSelectVehicle={handleSelectVehicle}
      />

      {previewData && (
        <PreviewPDF
          open={openPreviewModal}
          title={previewData.fileName || Vocabulary.generateDocument}
          onClose={() => {
            setOpenPreviewModal(false);
            setPreviewData(null);
            handleClose();
          }}
          signature={true}
          isSign={previewData.isSignature === "1" ? false : true}
          url={previewData.file}
          resetClientFilePathForGDPR={() => {
            setOpenPreviewModal(false);
            setPreviewData(null);
            handleClose();
          }}
        />
      )}
    </GenericModal>
  );
}
